import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, LogOut, Loader2, Users, FileText, MessageSquare, CalendarClock,
  CheckCircle2, XCircle, Upload, Trash2, Send, Plus, Save, Search, Wallet, Lock, DollarSign,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  fetchMe, fetchAdminClients, fetchAdminClient, logout,
  adminCreateCase, adminUpdateCase, adminToggleMilestone,
  adminUploadDocument, adminDeleteDocument,
  adminSendMessage, adminUpdateBooking,
  adminCreateEngagement, adminDeleteEngagement,
} from "@/lib/api";

function formatWhen(iso) {
  try { return new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}
function formatBytes(b) {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export default function Admin() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [clients, setClients] = useState(null);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const loadClients = useCallback(() => fetchAdminClients().then((d) => setClients(d.clients)), []);

  useEffect(() => {
    fetchMe()
      .then((u) => {
        if (u.role !== "admin") { navigate("/portal", { replace: true }); return; }
        setMe(u);
        loadClients();
      })
      .catch(() => navigate("/login", { replace: true }));
  }, [navigate, loadClients]);

  const doLogout = async () => { try { await logout(); } catch {} navigate("/login", { replace: true }); };

  if (!me || !clients) return <div className="min-h-[70vh] flex items-center justify-center"><Loader2 size={28} className="text-teal animate-spin" /></div>;

  const filtered = clients.filter((c) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (c.name || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q);
  });

  return (
    <div data-testid="admin-page" className="bg-sand min-h-[80vh]">
      <section className="bg-brand-gradient text-canvas grain relative overflow-hidden">
        <div className="container-pad py-14 relative">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <Avatar className="w-14 h-14 border-2 border-white/30">
                <AvatarImage src={me?.picture} alt={me?.name} />
                <AvatarFallback className="bg-canvas text-navy font-display">{me?.name?.[0] || "A"}</AvatarFallback>
              </Avatar>
              <div>
                <div className="overline" style={{ color: '#A7E0E6' }}>Admin dashboard</div>
                <h1 className="font-display text-3xl mt-1">Elevate control room</h1>
                <div className="text-canvas/70 text-sm mt-1">Signed in as {me.email}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/portal" className="btn-light-on-navy text-sm"><ArrowLeft size={14} /> Client view</Link>
              <button data-testid="admin-logout" onClick={doLogout} className="btn-light-on-navy text-sm"><LogOut size={14} /> Sign out</button>
            </div>
          </div>
        </div>
      </section>

      <section className="container-pad py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Client list */}
        <aside className="lg:col-span-4">
          <div className="card-soft p-6 sticky top-28">
            <div className="flex items-center justify-between">
              <div className="overline flex items-center gap-2"><Users size={14} /> Clients ({clients.length})</div>
            </div>
            <div className="relative mt-4">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted2" />
              <Input placeholder="Search by name or email" value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9" />
            </div>
            <ul className="mt-4 space-y-2 max-h-[70vh] overflow-y-auto pr-1">
              {filtered.length === 0 && <li className="text-muted2 text-sm text-center py-6">No clients yet.</li>}
              {filtered.map((c) => {
                const active = selectedId === c.user_id;
                return (
                  <li key={c.user_id}>
                    <button
                      onClick={() => setSelectedId(c.user_id)}
                      data-testid={`admin-client-${c.user_id}`}
                      className={`w-full text-left p-3 rounded-xl transition-all ${
                        active ? "bg-brand-gradient text-canvas shadow-lg" : "hover:bg-sand text-ink"
                      }`}
                    >
                      <div className={`font-medium ${active ? "text-canvas" : "text-navy"}`}>{c.name}</div>
                      <div className={`text-xs mt-0.5 ${active ? "text-canvas/70" : "text-muted2"}`}>{c.email}</div>
                      <div className="mt-2 flex items-center gap-3 text-[11px]">
                        <span className={active ? "text-canvas/80" : "text-muted2"}>{c.case_count} cases</span>
                        {c.unread_messages > 0 && (
                          <span className="bg-teal text-white px-2 py-0.5 rounded-full">{c.unread_messages} new</span>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>

        {/* Detail */}
        <div className="lg:col-span-8">
          {selectedId ? (
            <ClientDetail userId={selectedId} onChanged={loadClients} />
          ) : (
            <div className="card-soft p-16 text-center">
              <Users size={40} className="text-teal mx-auto" />
              <h3 className="font-display text-2xl text-navy mt-6">Pick a client</h3>
              <p className="text-muted2 mt-3 max-w-sm mx-auto text-sm">
                Choose a client from the list to view cases, upload documents, exchange messages and manage bookings.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

/* -------- CLIENT DETAIL -------- */
function ClientDetail({ userId, onChanged }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("cases");

  const reload = useCallback(async () => {
    const d = await fetchAdminClient(userId);
    setData(d);
    onChanged?.();
  }, [userId, onChanged]);

  useEffect(() => { reload(); }, [reload]);

  if (!data) return <div className="card-soft p-10"><Loader2 size={20} className="text-teal animate-spin" /></div>;

  const { client, cases, documents, messages, bookings, engagements } = data;
  const TABS = [
    { key: "cases",       label: "Cases",       icon: CheckCircle2 },
    { key: "billing",     label: "Billing",     icon: Wallet },
    { key: "documents",   label: "Documents",   icon: FileText },
    { key: "messages",    label: "Messages",    icon: MessageSquare },
    { key: "bookings",    label: "Bookings",    icon: CalendarClock },
  ];

  return (
    <div className="space-y-6">
      <div className="card-soft p-6 flex items-center justify-between gap-4">
        <div>
          <div className="overline">Client</div>
          <div className="font-display text-2xl text-navy mt-1">{client.name}</div>
          <div className="text-muted2 text-sm">{client.email}</div>
        </div>
        <div className="text-xs text-muted2 text-right">
          <div>Joined {formatWhen(client.created_at)}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
                  data-testid={`admin-tab-${t.key}`}
                  className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full transition-all ${
                    tab === t.key ? "bg-navy text-canvas" : "bg-white text-navy border border-hairline hover:bg-sand"
                  }`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "cases"       && <CasesTab client={client} cases={cases} reload={reload} />}
      {tab === "billing"     && <BillingTab client={client} engagements={engagements || []} reload={reload} />}
      {tab === "documents"   && <DocumentsTab client={client} documents={documents} reload={reload} />}
      {tab === "messages"    && <MessagesTab client={client} messages={messages} reload={reload} />}
      {tab === "bookings"    && <BookingsTab bookings={bookings} reload={reload} />}
    </div>
  );
}

/* ---- Cases tab ---- */
function CasesTab({ client, cases, reload }) {
  const [creating, setCreating] = useState(false);
  const [newCase, setNewCase] = useState({ title: "", stage: "Discovery", next_action: "" });
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!newCase.title) return toast.error("Title required");
    setSaving(true);
    try {
      await adminCreateCase({ user_id: client.user_id, ...newCase, progress: 0 });
      toast.success("Case created");
      setNewCase({ title: "", stage: "Discovery", next_action: "" });
      setCreating(false);
      await reload();
    } catch { toast.error("Couldn't create case"); }
    setSaving(false);
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <div className="overline">Cases ({cases.length})</div>
        <button onClick={() => setCreating(!creating)} data-testid="admin-new-case" className="btn-ghost-navy text-sm !py-2 !px-4">
          <Plus size={14} /> New case
        </button>
      </div>

      {creating && (
        <div className="card-soft p-6 space-y-3">
          <Label className="text-xs text-muted2">Case title</Label>
          <Input value={newCase.title} onChange={(e) => setNewCase({ ...newCase, title: e.target.value })}
                 placeholder="e.g. NDIS Registration onboarding" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-muted2">Stage</Label>
              <Input value={newCase.stage} onChange={(e) => setNewCase({ ...newCase, stage: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-muted2">Next action</Label>
              <Input value={newCase.next_action} onChange={(e) => setNewCase({ ...newCase, next_action: e.target.value })} />
            </div>
          </div>
          <button onClick={create} disabled={saving} className="btn-ochre">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Create
          </button>
        </div>
      )}

      {cases.map((c) => <CaseCard key={c.case_id} c={c} reload={reload} />)}
    </div>
  );
}

function CaseCard({ c, reload }) {
  const [edit, setEdit] = useState({ title: c.title, stage: c.stage, next_action: c.next_action, progress: c.progress });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await adminUpdateCase(c.case_id, edit);
      toast.success("Case updated");
      await reload();
    } catch { toast.error("Update failed"); }
    setSaving(false);
  };

  const toggleMilestone = async (key, next) => {
    try {
      await adminToggleMilestone(c.case_id, key, next);
      await reload();
    } catch { toast.error("Milestone update failed"); }
  };

  return (
    <div className="card-soft p-6" data-testid={`admin-case-${c.case_id}`}>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        <div className="md:col-span-6">
          <Label className="text-xs text-muted2">Title</Label>
          <Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} />
        </div>
        <div className="md:col-span-3">
          <Label className="text-xs text-muted2">Stage</Label>
          <Input value={edit.stage} onChange={(e) => setEdit({ ...edit, stage: e.target.value })} />
        </div>
        <div className="md:col-span-3">
          <Label className="text-xs text-muted2">Progress %</Label>
          <Input type="number" min={0} max={100} value={edit.progress}
                 onChange={(e) => setEdit({ ...edit, progress: parseInt(e.target.value || 0) })} />
        </div>
        <div className="md:col-span-12">
          <Label className="text-xs text-muted2">Next action</Label>
          <Input value={edit.next_action} onChange={(e) => setEdit({ ...edit, next_action: e.target.value })} />
        </div>
      </div>
      <div className="flex justify-end mt-3">
        <button onClick={save} disabled={saving} className="btn-ochre text-sm !py-2 !px-4">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
        </button>
      </div>

      <div className="mt-6">
        <div className="overline">Elevate Method milestones</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
          {(c.milestones || []).map((m, idx) => (
            <button key={m.key} onClick={() => toggleMilestone(m.key, !m.completed)}
                    data-testid={`admin-milestone-${c.case_id}-${m.key}`}
                    className={`rounded-xl p-3 text-center text-xs transition-colors ${
                      m.completed ? "bg-teal/10 border border-teal/40" : "bg-sand border border-hairline hover:border-teal/30"
                    }`}>
              <div className={`font-display text-lg ${m.completed ? "text-teal" : "text-navy/40"}`}>{String(idx+1).padStart(2,'0')}</div>
              <div className={`mt-1 font-medium ${m.completed ? "text-navy" : "text-muted2"}`}>{m.title}</div>
              {m.completed && <CheckCircle2 size={14} className="text-teal mx-auto mt-2" />}
            </button>
          ))}
        </div>
        <Progress value={c.progress} className="mt-4 h-1.5" />
      </div>
    </div>
  );
}

/* ---- Billing tab ---- */
const PACKAGES = [
  { key: "verification",  label: "Verification Pathway (Low Risk)",  min: 2500, max: 4500 },
  { key: "certification", label: "Certification Pathway (High Risk)", min: 4500, max: 8000 },
  { key: "module_addon",  label: "Complex Registration Module",       min: 1000, max: 2000 },
  { key: "custom",        label: "Custom engagement",                 min: 0,    max: 100000 },
];

function BillingTab({ client, engagements, reload }) {
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ package: "verification", title: "", total_amount: 3500, installments: 1, notes: "" });
  const [saving, setSaving] = useState(false);
  const pkg = PACKAGES.find((p) => p.key === form.package);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    if (form.package !== "custom" && (form.total_amount < pkg.min || form.total_amount > pkg.max))
      return toast.error(`Amount must be between $${pkg.min} and $${pkg.max}`);
    setSaving(true);
    try {
      await adminCreateEngagement({ user_id: client.user_id, ...form });
      toast.success("Engagement created and client notified");
      setForm({ package: "verification", title: "", total_amount: 3500, installments: 1, notes: "" });
      setCreating(false);
      await reload();
    } catch (err) { toast.error("Create failed", { description: err?.response?.data?.detail }); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this engagement and all instalments? Payments already made will remain in Stripe.")) return;
    try { await adminDeleteEngagement(id); await reload(); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <div className="overline">Engagements ({engagements.length})</div>
        <button onClick={() => setCreating(!creating)} data-testid="admin-new-engagement" className="btn-ochre text-sm !py-2 !px-4">
          <Plus size={14}/> New engagement
        </button>
      </div>

      {creating && (
        <div className="card-soft p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted2">Package</Label>
              <Select value={form.package} onValueChange={(v) => {
                const p = PACKAGES.find((x) => x.key === v);
                setForm({ ...form, package: v, total_amount: p.min || form.total_amount });
              }}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PACKAGES.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted2 mt-1">
                {pkg && pkg.key !== "custom" ? `Range: $${pkg.min.toLocaleString()} – $${pkg.max.toLocaleString()} AUD` : "Any amount"}
              </p>
            </div>
            <div>
              <Label className="text-xs text-muted2">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                     placeholder="e.g. NDIS Certification – Q1 2026" />
            </div>
            <div>
              <Label className="text-xs text-muted2">Total amount (AUD)</Label>
              <div className="relative">
                <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted2" />
                <Input type="number" step="0.01" className="pl-8"
                       value={form.total_amount}
                       onChange={(e) => setForm({ ...form, total_amount: parseFloat(e.target.value || 0) })} />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted2">Instalments</Label>
              <Select value={String(form.installments)} onValueChange={(v) => setForm({ ...form, installments: parseInt(v) })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1,2,3,4,5,6].map((n) => <SelectItem key={n} value={String(n)}>{n} instalment{n>1?"s":""}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted2 mt-1">
                ${(form.total_amount / form.installments).toFixed(2)} per instalment
              </p>
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted2">Notes (visible to client)</Label>
            <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="Optional scope summary or terms" />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setCreating(false)} className="btn-ghost-navy text-sm !py-2 !px-4">Cancel</button>
            <button onClick={create} disabled={saving} className="btn-ochre text-sm !py-2 !px-4">
              {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Create engagement
            </button>
          </div>
        </div>
      )}

      {engagements.length === 0 && !creating && (
        <div className="card-soft p-10 text-center">
          <Wallet size={32} className="text-teal mx-auto"/>
          <p className="text-muted2 text-sm mt-4">No engagements yet. Create one to charge this client.</p>
        </div>
      )}

      {engagements.map((e) => {
        const outstanding = Math.max(0, e.total_amount - (e.amount_paid || 0));
        return (
          <div key={e.engagement_id} className="card-soft p-6" data-testid={`admin-engagement-${e.engagement_id}`}>
            <div className="flex justify-between items-start gap-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-teal">{e.package_label}</div>
                <div className="font-display text-xl text-navy mt-1">{e.title}</div>
                {e.notes && <div className="text-sm text-muted2 mt-2">{e.notes}</div>}
                <div className="mt-3 flex flex-wrap gap-2">
                  <StatusBadge status={e.status}/>
                  {e.docs_unlocked
                    ? <span className="inline-flex items-center gap-1 text-teal text-xs bg-teal/10 border border-teal/20 rounded-full px-3 py-1"><CheckCircle2 size={12}/> Docs unlocked</span>
                    : <span className="inline-flex items-center gap-1 text-navy text-xs bg-sand border border-hairline rounded-full px-3 py-1"><Lock size={12}/> Docs locked</span>}
                </div>
              </div>
              <div className="text-right">
                <div className="font-display text-2xl text-navy">${e.total_amount.toLocaleString("en-AU",{minimumFractionDigits:2})}</div>
                <div className="text-xs text-teal mt-1">Paid ${(e.amount_paid || 0).toLocaleString("en-AU",{minimumFractionDigits:2})}</div>
                <div className="text-xs text-muted2 mt-1">Outstanding ${outstanding.toLocaleString("en-AU",{minimumFractionDigits:2})}</div>
                <button onClick={() => del(e.engagement_id)}
                        className="mt-3 text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1">
                  <Trash2 size={12}/> Delete
                </button>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-hairline">
              <div className="overline">Instalments</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-3">
                {(e.installments || []).map((i) => (
                  <div key={i.installment_id} className={`rounded-xl p-3 text-sm ${
                    i.status === "paid" ? "bg-teal/10 border border-teal/30" :
                    i.status === "waived" ? "bg-sand border border-hairline text-muted2" :
                    "bg-white border border-hairline"
                  }`}>
                    <div className="flex justify-between items-center">
                      <span className="font-display text-navy">#{i.sequence}</span>
                      <span className="text-xs uppercase tracking-widest">{i.status}</span>
                    </div>
                    <div className="font-display text-lg text-navy mt-1">${i.amount.toLocaleString("en-AU",{minimumFractionDigits:2})}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    paid_in_full: "bg-teal/10 text-teal border border-teal/20",
    partial:      "bg-yellow-100 text-yellow-800 border border-yellow-200",
    unpaid:       "bg-sand text-navy border border-hairline",
  };
  return <span className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold ${map[status] || map.unpaid}`}>{status?.replace('_',' ')}</span>;
}

/* ---- Documents tab ---- */
function DocumentsTab({ client, documents, reload }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await adminUploadDocument(client.user_id, file);
      toast.success(`${file.name} uploaded and client notified`);
      await reload();
    } catch { toast.error("Upload failed"); }
    setUploading(false);
    e.target.value = "";
  };

  const del = async (doc_id) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await adminDeleteDocument(doc_id);
      toast.success("Deleted");
      await reload();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="card-soft p-6">
      <div className="flex justify-between items-center">
        <div className="overline">Documents ({documents.length})</div>
        <div>
          <input ref={inputRef} type="file" hidden onChange={upload} data-testid="admin-doc-input" />
          <button onClick={() => inputRef.current?.click()} disabled={uploading}
                  data-testid="admin-doc-upload" className="btn-ochre text-sm !py-2 !px-4">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload document
          </button>
        </div>
      </div>
      {documents.length === 0 ? (
        <p className="text-muted2 text-sm mt-6">No documents shared yet.</p>
      ) : (
        <ul className="mt-6 divide-y divide-hairline">
          {documents.map((d) => (
            <li key={d.doc_id} className="flex items-center justify-between gap-4 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-teal/10 flex items-center justify-center"><FileText size={16} className="text-teal" /></div>
                <div>
                  <div className="font-medium text-navy">{d.original_name}</div>
                  <div className="text-xs text-muted2 mt-0.5">{formatBytes(d.size)} · {formatWhen(d.uploaded_at)}</div>
                </div>
              </div>
              <button onClick={() => del(d.doc_id)} className="text-red-600 hover:text-red-700 text-sm inline-flex items-center gap-1">
                <Trash2 size={14} /> Delete
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ---- Messages tab ---- */
function MessagesTab({ client, messages, reload }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await adminSendMessage(client.user_id, text.trim());
      setText("");
      await reload();
    } catch { toast.error("Send failed"); }
    setSending(false);
  };

  return (
    <div className="card-soft flex flex-col h-[65vh] overflow-hidden">
      <div className="p-5 border-b border-hairline">
        <div className="overline">Thread with {client.name}</div>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-sand">
        {messages.length === 0 ? (
          <p className="text-muted2 text-sm text-center py-10">No messages yet.</p>
        ) : (
          messages.map((m) => {
            const admin = m.sender_role === "admin";
            return (
              <div key={m.message_id} className={`flex ${admin ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] rounded-2xl p-4 ${admin ? "bg-navy text-canvas" : "bg-white text-ink border border-hairline"}`}>
                  <div className={`text-[10px] uppercase tracking-widest ${admin ? "text-teal-400" : "text-teal"}`}>
                    {admin ? "You" : m.sender_name} · {formatWhen(m.created_at)}
                  </div>
                  <div className="mt-2 text-sm whitespace-pre-wrap leading-relaxed">{m.body}</div>
                </div>
              </div>
            );
          })
        )}
      </div>
      <form onSubmit={send} className="p-4 border-t border-hairline flex items-end gap-3">
        <Textarea rows={2} data-testid="admin-msg-input" value={text} onChange={(e) => setText(e.target.value)}
                  placeholder="Reply to your client..." className="resize-none flex-1" />
        <button data-testid="admin-msg-send" disabled={sending || !text.trim()} className="btn-ochre !py-3 !px-5 disabled:opacity-60">
          {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </button>
      </form>
    </div>
  );
}

/* ---- Bookings tab ---- */
function BookingsTab({ bookings, reload }) {
  if (bookings.length === 0)
    return <div className="card-soft p-10 text-center"><CalendarClock size={32} className="text-teal mx-auto" /><p className="text-muted2 text-sm mt-4">No booking requests yet.</p></div>;

  return (
    <div className="space-y-3">
      {bookings.map((b) => <BookingRow key={b.booking_id} b={b} reload={reload} />)}
    </div>
  );
}

function BookingRow({ b, reload }) {
  const [meetingLink, setMeetingLink] = useState(b.meeting_link || "");
  const [adminNote, setAdminNote] = useState(b.admin_note || "");
  const [saving, setSaving] = useState(false);

  const act = async (status) => {
    setSaving(true);
    try {
      await adminUpdateBooking(b.booking_id, { status, meeting_link: meetingLink || null, admin_note: adminNote || null });
      toast.success(`Booking ${status}`);
      await reload();
    } catch { toast.error("Update failed"); }
    setSaving(false);
  };

  return (
    <div className="card-soft p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-display text-lg text-navy">{b.preferred_date} · {b.preferred_window}</div>
          <div className="text-sm text-muted2 mt-1">Requested by {b.user_name} ({b.user_email}) · {formatWhen(b.created_at)}</div>
          {b.notes && <div className="text-sm text-ink mt-3 bg-sand rounded-xl p-3 italic">"{b.notes}"</div>}
        </div>
        <StatusPill status={b.status} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
        <div>
          <Label className="text-xs text-muted2">Meeting link (sent to client on confirm)</Label>
          <Input value={meetingLink} onChange={(e) => setMeetingLink(e.target.value)} placeholder="https://meet.google.com/..." />
        </div>
        <div>
          <Label className="text-xs text-muted2">Note to client (optional)</Label>
          <Input value={adminNote} onChange={(e) => setAdminNote(e.target.value)} />
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        <button onClick={() => act("confirmed")} disabled={saving} className="btn-ochre text-sm !py-2 !px-4">
          <CheckCircle2 size={14} /> Confirm
        </button>
        <button onClick={() => act("cancelled")} disabled={saving}
                className="text-sm !py-2 !px-4 rounded-full border border-red-300 text-red-600 hover:bg-red-50 inline-flex items-center gap-2">
          <XCircle size={14} /> Cancel
        </button>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const styles = {
    pending:   "bg-yellow-100 text-yellow-800 border border-yellow-200",
    confirmed: "bg-teal/10 text-teal border border-teal/20",
    cancelled: "bg-red-100 text-red-700 border border-red-200",
  };
  return <span className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold ${styles[status] || styles.pending}`}>{status}</span>;
}
